import { FieldJsonModalBase } from '@shared/blockly/fields';

const BUTTON_SIZE = 16;
const BUTTON_MARGIN = 4;

/**
 * Custom Blockly field for JSON editing.
 * Always opens a modal code editor on click for comfortable JSON editing.
 * Shows a truncated preview in the block itself.
 */
export class FieldJsonModal extends FieldJsonModalBase {
    private overlay_: HTMLDivElement | null = null;
    private dialog_: HTMLDivElement | null = null;
    private textarea_: HTMLTextAreaElement | null = null;
    private expandButton_: SVGGElement | null = null;
    private errorHint_: HTMLDivElement | null = null;

    public override initView(): void {
        super.initView();
        this.createExpandButton_();
    }

    private createExpandButton_(): void {
        if (!this.fieldGroup_) return;

        const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        group.setAttribute('class', 'blocklyJsonBtn');
        group.setAttribute('cursor', 'pointer');

        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('rx', '3');
        rect.setAttribute('ry', '3');
        rect.setAttribute('width', String(BUTTON_SIZE));
        rect.setAttribute('height', String(BUTTON_SIZE));
        rect.setAttribute('fill', '#fff');
        rect.setAttribute('fill-opacity', '0.6');
        rect.setAttribute('stroke', '#888');
        rect.setAttribute('stroke-width', '0.5');
        group.appendChild(rect);

        const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        label.setAttribute('x', String(BUTTON_SIZE / 2));
        label.setAttribute('y', String(BUTTON_SIZE / 2 + 1));
        label.setAttribute('text-anchor', 'middle');
        label.setAttribute('dominant-baseline', 'central');
        label.setAttribute('font-size', '9');
        label.setAttribute('fill', '#555');
        label.setAttribute('pointer-events', 'none');
        label.textContent = '{ }';
        group.appendChild(label);

        this.fieldGroup_.appendChild(group);
        this.expandButton_ = group;
    }

    protected override showEditor_(_e?: Event): void {
        this.openJsonModal_();
    }

    protected override updateSize_(margin?: number): void {
        super.updateSize_(margin);
        this.size_.width += BUTTON_SIZE + BUTTON_MARGIN * 2;
    }

    protected override render_(): void {
        super.render_();
        this.positionExpandButton_();
    }

    private positionExpandButton_(): void {
        if (!this.expandButton_ || !this.borderRect_) return;
        const bbox = this.borderRect_.getBBox();
        const x = bbox.width + BUTTON_MARGIN;
        const y = (bbox.height - BUTTON_SIZE) / 2;
        this.expandButton_.setAttribute('transform', 'translate(' + String(x) + ', ' + String(y) + ')');
    }

    private openJsonModal_(): void {
        const currentValue = this.getValue() ?? '{}';

        // Pretty-print JSON if possible
        let formattedValue = currentValue;
        try {
            formattedValue = JSON.stringify(JSON.parse(currentValue), null, 2);
        } catch {
            // Keep as-is if not valid JSON
        }

        // Create overlay
        const overlay = document.createElement('div');
        overlay.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
      background: rgba(0,0,0,0.5); z-index: 10000;
      display: flex; align-items: center; justify-content: center;
    `;
        this.overlay_ = overlay;

        // Create dialog
        const dialog = document.createElement('div');
        dialog.style.cssText = `
      background: #1e1e1e; border-radius: 8px; padding: 20px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.5);
      min-width: 500px; max-width: 80vw; max-height: 80vh;
      display: flex; flex-direction: column; gap: 12px;
    `;
        this.dialog_ = dialog;

        // Title
        const title = document.createElement('div');
        title.textContent = 'JSON bearbeiten';
        title.style.cssText = 'font-size: 16px; font-weight: 600; color: #ccc;';
        dialog.appendChild(title);

        // Error hint
        const errorHint = document.createElement('div');
        errorHint.style.cssText = `
      font-size: 12px; color: #f44; padding: 4px 8px;
      border-radius: 4px; background: rgba(255,68,68,0.1);
      display: none;
    `;
        this.errorHint_ = errorHint;
        dialog.appendChild(errorHint);

        // Textarea (code editor style)
        const textarea = document.createElement('textarea');
        textarea.value = formattedValue;
        textarea.spellcheck = false;
        textarea.style.cssText = `
      width: 100%; min-height: 300px; padding: 12px;
      font-family: 'Fira Code', 'Consolas', 'Monaco', monospace;
      font-size: 14px; line-height: 1.5; tab-size: 2;
      border: 1px solid #444; border-radius: 4px;
      resize: both; outline: none; box-sizing: border-box;
      background: #2d2d2d; color: #d4d4d4;
    `;
        textarea.addEventListener('focus', () => {
            textarea.style.borderColor = '#4C97FF';
            textarea.style.boxShadow = '0 0 0 2px rgba(76,151,255,0.2)';
        });
        textarea.addEventListener('blur', () => {
            textarea.style.borderColor = '#444';
            textarea.style.boxShadow = 'none';
        });
        textarea.addEventListener('input', () => {
            this.validateJson_(textarea.value);
        });
        textarea.addEventListener('keydown', (e: KeyboardEvent) => {
            // Tab-Unterstützung
            if (e.key === 'Tab') {
                e.preventDefault();
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                textarea.value = textarea.value.substring(0, start) + '  ' + textarea.value.substring(end);
                textarea.selectionStart = textarea.selectionEnd = start + 2;
            }
        });
        this.textarea_ = textarea;
        dialog.appendChild(textarea);

        // Button row
        const buttonRow = document.createElement('div');
        buttonRow.style.cssText = 'display: flex; justify-content: space-between; align-items: center;';

        const formatBtn = document.createElement('button');
        formatBtn.textContent = 'Formatieren';
        formatBtn.style.cssText = `
      padding: 8px 16px; border: 1px solid #555; border-radius: 4px;
      background: #333; color: #ccc; font-size: 13px; cursor: pointer;
    `;
        formatBtn.addEventListener('click', () => {
            try {
                textarea.value = JSON.stringify(JSON.parse(textarea.value), null, 2);
                this.validateJson_(textarea.value);
            } catch {
                // Already shown by validateJson_
            }
        });
        formatBtn.addEventListener('mouseenter', () => { formatBtn.style.background = '#444'; });
        formatBtn.addEventListener('mouseleave', () => { formatBtn.style.background = '#333'; });

        const rightBtns = document.createElement('div');
        rightBtns.style.cssText = 'display: flex; gap: 8px;';

        const cancelBtn = document.createElement('button');
        cancelBtn.textContent = 'Abbrechen';
        cancelBtn.style.cssText = `
      padding: 8px 20px; border: 1px solid #555; border-radius: 4px;
      background: #333; color: #ccc; font-size: 14px; cursor: pointer;
    `;
        cancelBtn.addEventListener('click', () => { this.closeModal_(false); });
        cancelBtn.addEventListener('mouseenter', () => { cancelBtn.style.background = '#444'; });
        cancelBtn.addEventListener('mouseleave', () => { cancelBtn.style.background = '#333'; });

        const saveBtn = document.createElement('button');
        saveBtn.textContent = 'Übernehmen';
        saveBtn.style.cssText = `
      padding: 8px 20px; border: none; border-radius: 4px;
      background: #4C97FF; color: #fff; font-size: 14px; cursor: pointer;
    `;
        saveBtn.addEventListener('click', () => { this.closeModal_(true); });
        saveBtn.addEventListener('mouseenter', () => { saveBtn.style.background = '#3A78D0'; });
        saveBtn.addEventListener('mouseleave', () => { saveBtn.style.background = '#4C97FF'; });

        rightBtns.appendChild(cancelBtn);
        rightBtns.appendChild(saveBtn);
        buttonRow.appendChild(formatBtn);
        buttonRow.appendChild(rightBtns);
        dialog.appendChild(buttonRow);

        // Key handler
        overlay.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                this.closeModal_(false);
            } else if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                this.closeModal_(true);
            }
        });

        // Click on overlay background closes
        overlay.addEventListener('mousedown', (e) => {
            if (e.target === overlay) {
                this.closeModal_(false);
            }
        });

        overlay.appendChild(dialog);
        document.body.appendChild(overlay);

        // Focus textarea
        requestAnimationFrame(() => {
            textarea.focus();
            textarea.setSelectionRange(0, 0);
        });
    }

    private validateJson_(value: string): void {
        if (!this.errorHint_) return;
        try {
            JSON.parse(value);
            this.errorHint_.style.display = 'none';
        } catch (e: any) {
            this.errorHint_.textContent = `JSON ungültig: ${e.message}`;
            this.errorHint_.style.display = 'block';
        }
    }

    private closeModal_(save: boolean): void {
        if (save && this.textarea_) {
            // Kompakt speichern (ohne Whitespace) für die Code-Generierung
            let val = this.textarea_.value;
            val = this.normalizeEditorValueForSave(val);
            this.setValue(val);
        }

        if (this.overlay_) {
            this.overlay_.remove();
            this.overlay_ = null;
        }
        this.dialog_ = null;
        this.textarea_ = null;
        this.errorHint_ = null;
    }

}
